import { Group, IconInfo, Typography } from '@screentone/core';

import { ScheduleDialog } from 'components/schedule-dialog/ScheduleDialog';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { formatInTimeZone } from 'utils/dates';
import { safelyParseContent } from 'utils/temp';
import styles from './SchedulePageDialog.module.scss';

interface SchedulePagePublishDialogProps {
  onSchedule: (publishUtc: number) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const SchedulePageDialog = ({ onSchedule, onCancel, isLoading = false }: SchedulePagePublishDialogProps) => {
  const { getEmbargoContent } = useDataModelContext();
  const { data: publicationSettingsResp } = usePublicationSettings();

  const publicationSetting = publicationSettingsResp?.publicationSetting;

  const latestEmbargoArticle = getEmbargoContent()
    .sort((a, b) => {
      const aDateTime = safelyParseContent<AllessehContent>(a).data.attributes.embargo_datetime_utc;
      const bDateTime = safelyParseContent<AllessehContent>(b).data.attributes.embargo_datetime_utc;

      const aDate = new Date(aDateTime!);
      const bDate = new Date(bDateTime!);

      return aDate < bDate ? 1 : -1;
    })
    .at(0);
  const embargoContent = latestEmbargoArticle ? safelyParseContent<AllessehContent>(latestEmbargoArticle) : undefined;

  const latestEmbargoTimestamp = embargoContent
    ? new Date(embargoContent.data.attributes.embargo_datetime_utc!).getTime()
    : undefined;

  let afterCalendarContent = publicationSetting ? (
    <Typography variant="note">
      All times are in {publicationSetting.preferredTimezone.timezoneAbbreviation}.
    </Typography>
  ) : null;

  if (embargoContent && publicationSetting) {
    afterCalendarContent = (
      <Group gap="md">
        <Group gap="sm" className={styles.latestEmbargo}>
          <IconInfo className={styles.latestEmbargoIcon} size="md" />
          <Typography
            margin={{ right: 'xs' }}
            className={styles.latestEmbargoInfo}
            data-testid="embargo-schedule-dialog"
          >
            Embargoed story for{' '}
            {formatInTimeZone(
              embargoContent.data.attributes.embargo_datetime_utc!,
              publicationSetting.preferredTimezone,
              "M/dd/yyyy 'at' hh:mm aaaa",
              { showTimezoneAbbreviation: true }
            )}
          </Typography>
        </Group>
        <Typography variant="note">
          All times are in {publicationSetting.preferredTimezone.timezoneAbbreviation}.
        </Typography>
      </Group>
    );
  }

  return (
    <ScheduleDialog
      dialogTitle="Schedule Page"
      onSchedule={onSchedule}
      onCancel={onCancel}
      isLoading={isLoading}
      afterCalendarContent={afterCalendarContent}
      publicationSetting={publicationSetting}
      latestEmbargoTimestamp={latestEmbargoTimestamp}
      hasEmbargo={!!latestEmbargoArticle}
    />
  );
};
